import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';
import { useAuthStore } from '../state/authStore.ts';

/**
 * About page. B2B procurement always Googles "[company] about" before signing — its absence is
 * a trust hit. Keep this page short, founder-led, and concrete about what we do and don't do.
 *
 * TODO: USER INPUT REQUIRED — replace the bio sections marked TODO with your actual background,
 * mission story, and (ideally) a real photo at web/public/founder.jpg.
 */
export default function AboutPage() {
  useSeo({
    title: 'About EAA Proof',
    canonicalPath: '/about',
    description:
      'EAA Proof is built by a Bulgarian software engineer to give SMBs and agencies a real WCAG audit + EN 301 549 accessibility statement without overlays or enterprise contracts.',
  });

  const user = useAuthStore(s => s.user);
  const scanCtaTarget = user ? '/' : '/sign-in';

  return (
    <div className="page about-page">
      <header className="about-hero">
        <p className="about-eyebrow">About</p>
        <h1>A real accessibility audit that fits your budget</h1>
        <p className="about-tagline">
          EAA Proof is a self-serve compliance tool built for the businesses caught between
          €50/month overlay widgets and €10,000/year enterprise platforms.
        </p>
      </header>

      <section className="about-section">
        <h2>What we do</h2>
        <p>
          We run an automated WCAG 2.1 AA accessibility audit on your website using the
          open-source axe-core engine — the same engine that powers Google Lighthouse and is
          referenced in the W3C Accessibility Conformance Testing repository. You get back a
          downloadable PDF audit, a draft accessibility statement HTML based on the European
          Commission's model template (Implementing Decision (EU) 2018/1523), and plain-English
          remediation guidance grounded in your actual page code.
        </p>
        <p>
          One scan, €29. No subscription, no widget on your site, no consultant invoice, no
          procurement cycle. If you need ongoing checks, our €9/month plan covers 5 scans and
          our €49/month agency plan covers 50 scans with your branding on the reports.
        </p>
      </section>

      <section className="about-section">
        <h2>What we don't do, and what we are not</h2>
        <p>
          We are not affiliated with, endorsed by, or representing the European Union, the
          European Commission, any EU regulatory body, or any authority responsible for the
          European Accessibility Act. We do not issue compliance certificates or guarantees.
          We do not provide legal advice. We do not sell a JavaScript "auto-fix" overlay
          widget. We do not promise instant compliance without code changes. We do not sell
          a multi-product enterprise platform. We do not lock you into annual contracts.
        </p>
        <p>
          The honest truth about automated accessibility testing: tools like axe-core catch
          roughly 30–40% of WCAG issues — the deterministic ones like missing alt text,
          contrast failures, and ARIA misuse. Keyboard-only flows, screen-reader UX, and
          cognitive load criteria still need a qualified human reviewer. For regulated
          industries (banking, ticketing, large e-commerce) you also need a manual review
          by a certified accessibility professional and review by qualified legal counsel
          before relying on any accessibility statement. EAA Proof is a useful starting
          point and ongoing monitoring tool — we tell you exactly what we catch and what
          we don't.
        </p>
      </section>

      <section className="about-section about-founder">
        <h2>Why this exists</h2>
        <p>
          EAA Proof is built and run by a single independent EU-based software engineer. It
          started after watching small businesses scramble to comply with the European
          Accessibility Act ahead of its June 2025 enforcement date — and finding that the
          only practical options were a $50/month overlay widget that regulators don't
          accept, or a five-figure annual contract with an enterprise auditing platform.
        </p>
        <p>
          The middle ground — a real audit at a real price — didn't exist. So we built it.
          The scan engine uses axe-core, the open-source de-facto standard. The statement
          generator follows the European Commission's model accessibility statement template
          (Implementing Decision (EU) 2018/1523) that EU regulators publish.
          The remediation guidance is grounded in your actual HTML, with five layers of
          anti-hallucination guards so the AI never invents a fix that doesn't apply to your
          page.
        </p>
        <p>
          We're a small operation. That's a feature, not a bug — every email to{' '}
          <a href="mailto:support@eaaproof.com">support@eaaproof.com</a> reaches the person who
          writes the code.
        </p>
      </section>

      <section className="about-section">
        <h2>Where we stand on the technical questions</h2>
        <ul className="about-list">
          <li>
            <strong>Engine:</strong> axe-core 4.10 — the open-source standard, also used by
            Microsoft, Google, Adobe, and the W3C.
          </li>
          <li>
            <strong>Browser:</strong> Real Chromium via Microsoft Playwright. We see the same
            DOM a real visitor sees, including JS-rendered content.
          </li>
          <li>
            <strong>Standard:</strong> WCAG 2.1 AA — the level referenced by EN 301 549 and the
            EAA.
          </li>
          <li>
            <strong>Output format:</strong> EU model accessibility statement (Implementing
            Decision (EU) 2018/1523) + PDF audit cited to specific WCAG success criteria.
          </li>
          <li>
            <strong>Data residency:</strong> Hetzner EU (Helsinki + Falkenstein) + Cloudflare
            R2 in EU jurisdiction. Reports never leave the EU.
          </li>
          <li>
            <strong>AI:</strong> Anthropic Claude Sonnet 4.6 for remediation writing only,
            with five anti-hallucination guards. Detection is deterministic (axe-core), never AI.
          </li>
        </ul>
      </section>

      <section className="about-cta">
        <h2>Try it before you buy</h2>
        <p>
          See a complete sample report — no sign-up required. Then run your own for €29.
        </p>
        <div className="about-cta-buttons">
          <Link to="/sample" className="cta cta-secondary">
            See a sample report
          </Link>
          <Link to={scanCtaTarget} className="cta">
            Start a scan
          </Link>
        </div>
      </section>
    </div>
  );
}
